import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWorkspaceTabNavigation } from '../context/WorkspaceTabsContext';
import merchantService from '../services/merchantService';
import gameService from '../services/gameService';
import MerchantProviderConfigurationModal from '../components/MerchantProviderConfigurationModal';
import MerchantProviderCsvActions from '../components/MerchantProviderCsvActions';
import MerchantInfrastructureFields from '../components/MerchantInfrastructureFields';
import './MerchantDetail.css';
import { normalizeCredentials, isEnvComplete, resolveProviderStatus } from '../utils/providerConfiguration';
import MerchantPaymentGateways from '../components/MerchantPaymentGateways';
import { buildMerchantPaymentGatewayPayload } from '../utils/paymentGatewayPayload';
import Pagination from '../components/Pagination';
import FilterDropdown from '../components/FilterDropdown';
import SortableHeader from '../components/SortableHeader';
import { ArrowLeftIcon, SearchIcon, AlertIcon, CheckCircleIcon } from '../components/Icons';
import {
  normalizeCredentialFields
} from '../utils/credentialFields';
import {
  formatCountryOptionLabel,
  formatCurrencyOptionLabel,
  getCountryCode,
  getCurrencyCode
} from '../utils/gameLabels';
import { notifyMerchantUpdated } from '../utils/merchantEvents';

const providerTableHeaderStyle = {
  padding: '0.75rem',
  textAlign: 'left',
  fontSize: '0.8125rem',
  fontWeight: 700,
  color: 'var(--text-secondary)'
};

const MerchantDetail = () => {
  const navigate = useNavigate();
  const { closeCurrentTabAndNavigate } = useWorkspaceTabNavigation();
  const { id } = useParams();
  const { hasPermission, getDefaultRoute } = useAuth();
  const editMode = !!id;

  const canEdit = hasPermission('merchants', editMode ? 'edit' : 'create');
  const canManageProviders = canEdit;
  const canView = hasPermission('merchants', 'view');
  const canViewProviderFinancial = hasPermission('merchants', 'view_financial');
  const canEditProviderFinancial = hasPermission('merchants', 'edit_financial');

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [allProviders, setAllProviders] = useState([]);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [providersPagination, setProvidersPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });

  // Filter Options
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [sources, setSources] = useState([]);
  const [countries, setCountries] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  // Filter State
  const [providerSearchTerm, setProviderSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSubCategory, setFilterSubCategory] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [filterCurrency, setFilterCurrency] = useState('');
  const [providerSort, setProviderSort] = useState({ sortBy: '', sortOrder: 'asc' });

  const [savedProviderSnapshot, setSavedProviderSnapshot] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    merchant_code: '',
    client_code: '',
    providers: [],
    payment_gateways: [],
    country: '',
    currency: '',
    cloudflare_name_servers: [''],
    is_client_vpn: false,
    is_client_sms_otp: false,
    is_client_email_otp: false,
    is_active: true,
    production_frontend_urls: [''],
    production_backend_urls: [''],
    production_api_urls: [''],
    production_api_key: '',
    production_api_secret: '',
    production_server_ips: [''],
    production_api_ips: [''],
    production_client_ips: [''],
    staging_frontend_urls: [''],
    staging_backend_urls: [''],
    staging_api_urls: [''],
    staging_api_key: '',
    staging_api_secret: '',
    staging_server_ips: [''],
    staging_api_ips: [''],
    staging_client_ips: ['']
  });

  // Provider Modal State
  const [isProviderModalOpen, setIsProviderModalOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [activeSection, setActiveSection] = useState('details');
  const [environment, setEnvironment] = useState('production');

  const resolveCallbackDefs = (provider) => {
    if (Array.isArray(provider?.callback_urls)) return provider.callback_urls;
    const fromList = allProviders.find(p => p._id === provider?.provider_id)?.callback_urls;
    return Array.isArray(fromList) ? fromList : [];
  };

  const resolveCredentialFields = (provider, fieldName) => {
    if (Array.isArray(provider?.[fieldName])) return normalizeCredentialFields(provider[fieldName]);
    const fromList = allProviders.find(p => p._id === provider?.provider_id)?.[fieldName];
    return normalizeCredentialFields(fromList);
  };

  const resolveGroupLinks = (provider) => {
    const normalizeLinks = (links = []) => (
      Array.isArray(links)
        ? links.map((link, index) => (
            typeof link === 'string'
              ? { name: `Link ${index + 1}`, url: link }
              : { name: link?.name || `Link ${index + 1}`, url: link?.url || link?.link || '' }
          )).filter(link => link.url)
        : []
    );
    const merchantLinks = normalizeLinks(provider?.group_links);
    if (merchantLinks.length > 0) return merchantLinks;
    const masterLinks = normalizeLinks(provider?.master_provider_group_links);
    if (masterLinks.length > 0) return masterLinks;
    const masterProvider = allProviders.find(p => p._id === provider?.provider_id);
    return normalizeLinks(masterProvider?.group_links);
  };

  const applyCallbackDefaults = (provider) => {
    const callbackDefs = resolveCallbackDefs(provider);
    if (!callbackDefs.length) return provider;

    const next = { ...provider, callback_urls: callbackDefs };
    const baseCreds = next.credentials || {};
    const applyEnv = (env) => {
      const envCreds = { ...(baseCreds[env] || {}) };
      const existing = envCreds.callback_paths || {};
      const merged = { ...existing };
      callbackDefs.forEach(cb => {
        if (!merged[cb.name] || !String(merged[cb.name]).trim()) {
          merged[cb.name] = cb.path || '';
        }
      });
      envCreds.callback_paths = merged;
      return envCreds;
    };
    next.credentials = {
      ...baseCreds,
      production: applyEnv('production'),
      staging: applyEnv('staging')
    };
    return next;
  };

  const applyCredentialDefaults = (provider) => {
    const credentialFields = [
      ...resolveCredentialFields(provider, 'api_credential_keys'),
      ...resolveCredentialFields(provider, 'bo_credential_keys')
    ].filter(field => String(field.defaultValue || '').trim());

    if (!credentialFields.length) return provider;

    const next = { ...provider };
    const baseCreds = next.credentials || {};
    const applyEnv = (env) => {
      const envCreds = { ...(baseCreds[env] || {}) };
      credentialFields.forEach((field) => {
        if (!envCreds[field.key] || !String(envCreds[field.key]).trim()) {
          envCreds[field.key] = field.defaultValue;
        }
      });
      return envCreds;
    };

    next.credentials = {
      ...baseCreds,
      production: applyEnv('production'),
      staging: applyEnv('staging')
    };
    return next;
  };

  const applyProviderDefaults = (provider) => applyCredentialDefaults(applyCallbackDefaults(provider));

  const fetchProviders = useCallback(async (page = 1, limit = providersPagination.limit, activeSort = providerSort, clearList = false) => {
    try {
      setProvidersLoading(true);
      if (clearList) setAllProviders([]);
      const params = {
        page,
        limit,
        search: providerSearchTerm,
        type: filterType,
        source: filterSource,
        country: filterCountry,
        currency: filterCurrency,
        category: filterCategory,
        sub_category: filterSubCategory,
        sortBy: activeSort.sortBy,
        sortOrder: activeSort.sortOrder,
        is_active: true
      };
      Object.keys(params).forEach(k => { if (params[k] === '') delete params[k]; });

      const res = await gameService.getMasterProviders(params);
      if (res.success) {
        setAllProviders(res.data);
        setProvidersPagination(res.pagination);
      }
    } catch (err) {
      console.error('Error fetching providers:', err);
    } finally {
      setProvidersLoading(false);
    }
  }, [providersPagination.limit, providerSearchTerm, filterType, filterSource, filterCountry, filterCurrency, filterCategory, filterSubCategory, providerSort]);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const [catRes, subRes, srcRes, countryRes, currencyRes] = await Promise.all([
        gameService.getCategories({ limit: 1000 }),
        gameService.getSubCategories({ limit: 1000 }),
        gameService.getSources({ limit: 1000 }),
        gameService.getCountries({ limit: 1000, is_active: true }),
        gameService.getCurrencies({ limit: 1000, is_active: true })
      ]);
      if (catRes.success) setCategories(catRes.data);
      if (subRes.success) setSubCategories(subRes.data);
      if (srcRes.success) setSources(srcRes.data);
      if (countryRes.success) setCountries(countryRes.data);
      if (currencyRes.success) setCurrencies(currencyRes.data);
    } catch (err) {
      console.error('Failed to fetch filter options', err);
    }
  }, []);

  const fetchMerchant = useCallback(async ({ providersOnly = false } = {}) => {
    if (!editMode) return;
    try {
      if (!providersOnly) setLoading(true);
      const res = await merchantService.getMerchantById(id);
      if (res.success) {
        const m = res.data;
        const loaded = {
          name: m.name,
          merchant_code: m.merchant_code,
          client_code: m.client_code || '',
          payment_gateways: m.payment_gateways || [],
          providers: m.providers.map(p => ({
            provider_id: p.provider_id?._id || p.provider_id,
            name: p.provider_id?.provider_id?.name || 'Unknown',
            type: p.provider_id?.type || 'Any',
            source: p.provider_id?.source?.name || 'Any',
            country: getCountryCode(p.provider_id?.country),
            currency: getCurrencyCode(p.provider_id?.currency),
            master_provider_group_links: p.provider_id?.group_links || [],
            api_credential_keys: p.provider_id?.api_credential_keys || [],
            bo_credential_keys: p.provider_id?.bo_credential_keys || [],
            category: p.provider_id?.provider_id?.category?.name || '-',
            sub_category: p.provider_id?.provider_id?.sub_category?.name || '-',
            callback_urls: p.provider_id?.callback_urls || [],
            group_links: p.group_links?.length > 0 ? p.group_links : (p.provider_id?.group_links || []),
            credentials: normalizeCredentials(p.credentials),
            selling_rate: p.selling_rate || 0,
            status: resolveProviderStatus(p),
            is_active: p.is_active,
            is_client_line: p.is_client_line || false
          })),
          country: m.country?._id || m.country || '',
          currency: m.currency?._id || m.currency || '',
          cloudflare_name_servers: m.cloudflare_name_servers?.length > 0 ? m.cloudflare_name_servers : [''],
          is_client_vpn: m.is_client_vpn || false,
          is_client_sms_otp: m.is_client_sms_otp || false,
          is_client_email_otp: m.is_client_email_otp || false,
          is_active: m.is_active,
          production_frontend_urls: m.production_frontend_urls?.length > 0 ? m.production_frontend_urls : [''],
          production_backend_urls: m.production_backend_urls?.length > 0 ? m.production_backend_urls : [''],
          production_api_urls: m.production_api_urls?.length > 0 ? m.production_api_urls : [''],
          production_api_key: m.production_api_key || '',
          production_api_secret: m.production_api_secret || '',
          production_server_ips: m.production_server_ips?.length > 0 ? m.production_server_ips : [''],
          production_api_ips: m.production_api_ips?.length > 0 ? m.production_api_ips : [''],
          production_client_ips: m.production_client_ips?.length > 0 ? m.production_client_ips : [''],
          staging_frontend_urls: m.staging_frontend_urls?.length > 0 ? m.staging_frontend_urls : [''],
          staging_backend_urls: m.staging_backend_urls?.length > 0 ? m.staging_backend_urls : [''],
          staging_api_urls: m.staging_api_urls?.length > 0 ? m.staging_api_urls : [''],
          staging_api_key: m.staging_api_key || '',
          staging_api_secret: m.staging_api_secret || '',
          staging_server_ips: m.staging_server_ips?.length > 0 ? m.staging_server_ips : [''],
          staging_api_ips: m.staging_api_ips?.length > 0 ? m.staging_api_ips : [''],
          staging_client_ips: m.staging_client_ips?.length > 0 ? m.staging_client_ips : ['']
        };
        setFormData(previous => providersOnly ? { ...previous, providers: loaded.providers } : loaded);
        setSavedProviderSnapshot(JSON.stringify(loaded.providers));
      }
    } catch (err) {
      alert('Failed to fetch merchant details');
      navigate('/merchants');
    } finally {
      if (!providersOnly) setLoading(false);
    }
  }, [id, editMode, navigate]);

  useEffect(() => {
    if (!canView) {
      navigate(getDefaultRoute(), { replace: true });
      return;
    }
    // Deep check for specific Create/Edit permission
    if (editMode && !hasPermission('merchants', 'edit')) {
      navigate('/merchants');
      return;
    }
    if (!editMode && !hasPermission('merchants', 'create')) {
      navigate('/merchants');
      return;
    }

    fetchFilterOptions();
    fetchMerchant();
  }, [fetchFilterOptions, fetchMerchant, canView, hasPermission, getDefaultRoute, editMode, navigate]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchProviders(1, providersPagination.limit, providerSort);
    }, 500);
    return () => clearTimeout(timer);
  }, [providerSearchTerm, filterType, filterSource, filterCountry, filterCurrency, filterCategory, filterSubCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleProviderSortChange = (sortBy, sortOrder) => {
    const nextSort = { sortBy, sortOrder };
    if (providerSort.sortBy === nextSort.sortBy && providerSort.sortOrder === nextSort.sortOrder) return;

    setProviderSort(nextSort);
    fetchProviders(1, providersPagination.limit, nextSort, true);
  };

  const handleInputChange = (e) => {
    if (!canEdit) return;
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'merchant_code' ? value.toUpperCase() : value)
    }));
  };

  const handleArrayInputChange = (e, field, index) => {
    if (!canEdit) return;
    const { value } = e.target;
    setFormData(prev => {
      const newList = [...prev[field]];
      newList[index] = value;
      return { ...prev, [field]: newList };
    });
  };

  const addArrayItem = (field) => {
    if (!canEdit) return;
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const removeArrayItem = (field, index) => {
    if (!canEdit) return;
    setFormData(prev => {
      const newList = prev[field].filter((_, i) => i !== index);
      return { ...prev, [field]: newList.length > 0 ? newList : [''] };
    });
  };

  const toggleProviderAssignment = (p) => {
    if (!canManageProviders) return;
    const isAssigned = formData.providers.some(fp => fp.provider_id === p._id);

    if (isAssigned) {
      setFormData(prev => ({
        ...prev,
        providers: prev.providers.filter(fp => fp.provider_id !== p._id)
      }));
    } else {
      const newProvider = {
        provider_id: p._id,
        name: p.provider_id?.name || 'Unknown',
        type: p.type || 'Any',
        source: p.source?.name || 'Any',
        country: getCountryCode(p.country),
        currency: getCurrencyCode(p.currency),
        master_provider_group_links: p.group_links || [],
        api_credential_keys: p.api_credential_keys || [],
        bo_credential_keys: p.bo_credential_keys || [],
        category: p.provider_id?.category?.name || '-',
        sub_category: p.provider_id?.sub_category?.name || '-',
        callback_urls: p.callback_urls || [],
        group_links: p.group_links || [],
        credentials: normalizeCredentials(null),
        selling_rate: 0,
        status: 'draft',
        is_active: false,
        is_client_line: false
      };
      const withDefaults = applyProviderDefaults(newProvider);
      setFormData(prev => ({
        ...prev,
        providers: [...prev.providers, withDefaults]
      }));
      openProviderModal(withDefaults);
    }
  };

  const openProviderModal = (p) => {
    const normalized = {
      ...p,
      status: resolveProviderStatus(p),
      group_links: resolveGroupLinks(p),
      credentials: normalizeCredentials(p.credentials)
    };
    setEditingProvider(applyProviderDefaults(normalized));
    setIsProviderModalOpen(true);
  };

  const closeProviderModal = () => {
    setIsProviderModalOpen(false);
    setEditingProvider(null);
  };

  const saveProviderSettings = () => {
    if (!canManageProviders) return;
    setFormData(prev => ({
      ...prev,
      providers: prev.providers.map(p =>
        p.provider_id === editingProvider.provider_id ? editingProvider : p
      )
    }));
    setIsProviderModalOpen(false);
    setEditingProvider(null);
  };

  const providerTypes = useMemo(() => ['Seamless', 'Transfer'], []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) return;
    try {
      setSubmitting(true);

      // Clean up empty items from arrays before submitting
      const cleanedProviders = formData.providers.map(p => ({
        ...applyProviderDefaults(p),
        group_links: (p.group_links || [])
          .map((link, index) => {
            if (typeof link === 'string') {
              return { name: `Link ${index + 1}`, url: link.trim() };
            }
            return {
              name: String(link?.name || '').trim(),
              url: String(link?.url || '').trim()
            };
          })
          .filter(link => link.url)
      }));
      const cleanedData = {
        ...formData,
        ...(canManageProviders ? {
          providers: cleanedProviders,
          payment_gateways: formData.payment_gateways.map(item => buildMerchantPaymentGatewayPayload(item, {
            canViewFinancial: canViewProviderFinancial, canEditFinancial: canEditProviderFinancial
          }))
        } : {}),
        cloudflare_name_servers: formData.cloudflare_name_servers.filter(i => i.trim() !== ''),
        production_frontend_urls: formData.production_frontend_urls.filter(i => i.trim() !== ''),
        production_backend_urls: formData.production_backend_urls.filter(i => i.trim() !== ''),
        production_api_urls: formData.production_api_urls.filter(i => i.trim() !== ''),
        production_api_key: formData.production_api_key.trim(),
        production_api_secret: formData.production_api_secret.trim(),
        production_server_ips: formData.production_server_ips.filter(i => i.trim() !== ''),
        production_api_ips: formData.production_api_ips.filter(i => i.trim() !== ''),
        production_client_ips: formData.production_client_ips.filter(i => i.trim() !== ''),
        staging_frontend_urls: formData.staging_frontend_urls.filter(i => i.trim() !== ''),
        staging_backend_urls: formData.staging_backend_urls.filter(i => i.trim() !== ''),
        staging_api_urls: formData.staging_api_urls.filter(i => i.trim() !== ''),
        staging_api_key: formData.staging_api_key.trim(),
        staging_api_secret: formData.staging_api_secret.trim(),
        staging_server_ips: formData.staging_server_ips.filter(i => i.trim() !== ''),
        staging_api_ips: formData.staging_api_ips.filter(i => i.trim() !== ''),
        staging_client_ips: formData.staging_client_ips.filter(i => i.trim() !== '')
      };
      if (!canManageProviders) {
        delete cleanedData.providers;
        delete cleanedData.payment_gateways;
      }

      const response = editMode
        ? await merchantService.updateMerchant(id, cleanedData)
        : await merchantService.createMerchant(cleanedData);

      if (response.success) {
        notifyMerchantUpdated();
        navigate('/merchants');
      } else {
        alert(response.message || 'Failed to save merchant');
      }
    } catch (err) {
      const serverMsg = err?.response?.data?.message;
      alert(serverMsg || err?.message || 'An error occurred while saving merchant');
    } finally {
      setSubmitting(false);
    }
  };

  const modalMasterProvider = editingProvider ? allProviders.find(p => p._id === editingProvider.provider_id) : null;

  if (loading) return <div className="page-container" style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>;

  const sections = [
    { key: 'details', label: 'Basic details' },
    { key: 'infrastructure', label: 'Infrastructure' },
    { key: 'payments', label: 'Payment gateways', count: formData.payment_gateways.length },
    { key: 'games', label: 'Game providers', count: formData.providers.length }
  ];
  const handleSectionKeyDown = event => {
    const current = sections.findIndex(section => section.key === activeSection);
    const next = event.key === 'ArrowRight' ? (current + 1) % sections.length
      : event.key === 'ArrowLeft' ? (current + sections.length - 1) % sections.length
      : event.key === 'Home' ? 0 : event.key === 'End' ? sections.length - 1 : null;
    if (next === null) return;
    event.preventDefault();
    setActiveSection(sections[next].key);
    event.currentTarget.parentElement.querySelectorAll('[role="tab"]')[next].focus();
  };
  const showInvalidSection = event => {
    const panel = event.target.closest('[data-editor-section]');
    if (!panel?.hidden) return;
    event.preventDefault();
    const input = event.target;
    setActiveSection(panel.dataset.editorSection);
    requestAnimationFrame(() => input.reportValidity());
  };

  return (
    <div className="merchant-detail-page merchant-editor">
      <form onSubmit={handleSubmit} onInvalidCapture={showInvalidSection} className="merchant-editor-form">
        <header className="merchant-editor-heading">
          <button type="button" className="merchant-editor-back" aria-label="Back to merchants" onClick={() => closeCurrentTabAndNavigate('/merchants')}><ArrowLeftIcon size={18} /></button>
          <h2>{editMode ? formData.name || 'Merchant' : 'New merchant'}{formData.merchant_code && <span>{formData.merchant_code}</span>}</h2>
          <label className="merchant-editor-access"><span>Active</span><span className="switch">
            <input type="checkbox" name="is_active" aria-label="Merchant active" checked={formData.is_active} onChange={handleInputChange} disabled={!canEdit} />
            <span className="slider"></span>
          </span></label>
        </header>
        <div className="merchant-editor-tabs" role="tablist" aria-label="Merchant configuration">
          {sections.map(section => <button key={section.key} type="button" role="tab" id={`merchant-tab-${section.key}`}
            aria-selected={activeSection === section.key} aria-controls={`merchant-panel-${section.key}`} tabIndex={activeSection === section.key ? 0 : -1}
            onClick={() => setActiveSection(section.key)} onKeyDown={handleSectionKeyDown}>
            {section.label}{section.count !== undefined && <span>{section.count}</span>}
          </button>)}
        </div>
        <section className="merchant-editor-panel" id="merchant-panel-details" role="tabpanel" aria-labelledby="merchant-tab-details" data-editor-section="details" hidden={activeSection !== 'details'}>
          <div className="merchant-editor-details-grid">
            <label className="merchant-editor-field">Merchant Name *
              <input type="text" name="name" className="input" value={formData.name} onChange={handleInputChange} required placeholder="e.g. Asia Gaming" disabled={!canEdit} />
            </label>
            <label className="merchant-editor-field">Merchant Code (IT) *
              <input type="text" name="merchant_code" className="input merchant-editor-code" value={formData.merchant_code} onChange={handleInputChange} required placeholder="e.g. AG001" disabled={!canEdit} />
            </label>
            <label className="merchant-editor-field">Client Code (Finance)
              <input type="text" name="client_code" className="input" value={formData.client_code} onChange={handleInputChange} placeholder="e.g. FIN-AG001" disabled={!canEdit} />
            </label>
            <label className="merchant-editor-field">Country
              <select name="country" className="input" value={formData.country} onChange={handleInputChange} disabled={!canEdit}>
                <option value="">Select country</option>
                {countries.map(country => <option key={country._id} value={country._id}>{country.name} ({country.code})</option>)}
              </select>
            </label>
            <label className="merchant-editor-field">Currency
              <select name="currency" className="input" value={formData.currency} onChange={handleInputChange} disabled={!canEdit}>
                <option value="">Select currency</option>
                {currencies.map(currency => <option key={currency._id} value={currency._id}>{currency.currency_code || currency.code} - {currency.name}</option>)}
              </select>
            </label>
          </div>
          <fieldset className="merchant-editor-services">
            <legend>Extra services</legend>
            <label><input type="checkbox" name="is_client_vpn" checked={formData.is_client_vpn} onChange={handleInputChange} disabled={!canEdit} />VPN</label>
            <label><input type="checkbox" name="is_client_sms_otp" checked={formData.is_client_sms_otp} onChange={handleInputChange} disabled={!canEdit} />SMS OTP</label>
            <label><input type="checkbox" name="is_client_email_otp" checked={formData.is_client_email_otp} onChange={handleInputChange} disabled={!canEdit} />Email OTP</label>
          </fieldset>
          <section className="merchant-editor-nameservers" aria-labelledby="merchant-nameservers-heading">
            <h3 id="merchant-nameservers-heading">Cloudflare name servers</h3>
            <div className="merchant-editor-nameserver-fields">
              {formData.cloudflare_name_servers.map((server, index) => <div className="merchant-editor-array-row" key={index}>
                <input className="input" type="text" aria-label={`Cloudflare name server ${index + 1}`} placeholder="e.g. ada.ns.cloudflare.com" value={server}
                  onChange={event => handleArrayInputChange(event, 'cloudflare_name_servers', index)} disabled={!canEdit} />
                {canEdit && formData.cloudflare_name_servers.length > 1 && <button type="button" className="merchant-editor-remove" aria-label={`Remove name server ${index + 1}`} onClick={() => removeArrayItem('cloudflare_name_servers', index)}>×</button>}
              </div>)}
              {canEdit && <button type="button" className="merchant-editor-add" onClick={() => addArrayItem('cloudflare_name_servers')}>+ Add name server</button>}
            </div>
          </section>
        </section>
        <section className="merchant-editor-panel" id="merchant-panel-infrastructure" role="tabpanel" aria-labelledby="merchant-tab-infrastructure" data-editor-section="infrastructure" hidden={activeSection !== 'infrastructure'}>
          <div className="merchant-editor-environments" role="group" aria-label="Infrastructure environment">
            {['production', 'staging'].map(env => <button key={env} type="button" aria-pressed={environment === env} onClick={() => setEnvironment(env)}>{env === 'production' ? 'Production' : 'Staging'}</button>)}
          </div>
          <MerchantInfrastructureFields environment={environment} value={formData} disabled={!canEdit}
            onInputChange={handleInputChange} onArrayChange={handleArrayInputChange} onAdd={addArrayItem} onRemove={removeArrayItem} />
        </section>
        <section className="merchant-editor-panel" id="merchant-panel-payments" role="tabpanel" aria-labelledby="merchant-tab-payments" data-editor-section="payments" hidden={activeSection !== 'payments'}>
          <MerchantPaymentGateways value={formData.payment_gateways} embedded
            onChange={payment_gateways => setFormData(prev => ({ ...prev, payment_gateways }))}
            disabled={!canManageProviders || submitting}
            canViewFinancial={canViewProviderFinancial} canEditFinancial={canEditProviderFinancial} />
        </section>

        <section className="merchant-editor-panel merchant-editor-games" id="merchant-panel-games" role="tabpanel" aria-labelledby="merchant-tab-games" data-editor-section="games" hidden={activeSection !== 'games'}>
          <div className="merchant-editor-game-controls">
            {editMode && <div style={{ marginBottom: '1rem' }}><MerchantProviderCsvActions merchantId={id} merchantName={formData.name}
              importDisabled={submitting || !savedProviderSnapshot || JSON.stringify(formData.providers) !== savedProviderSnapshot}
              onImported={async () => { await fetchMerchant({ providersOnly: true }); await fetchProviders(); }} /></div>}
            <div className="merchant-editor-game-toolbar">
              <div>
                <div className="search-container" style={{ width: '100%', maxWidth: '100%' }}>
                  <div className="search-icon-wrapper"><SearchIcon /></div>
                  <input
                    type="text"
                    className="search-input"
                    aria-label="Search game providers" placeholder="Search by name, type, source or category…"
                    value={providerSearchTerm}
                    onChange={(e) => setProviderSearchTerm(e.target.value)}
                    style={{ width: '100%' }}
                  />
                </div>
              </div>

              <div className="merchant-editor-game-filters">
                <div>
                  <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Type</label>
                  <FilterDropdown
                    value={filterType}
                    onChange={setFilterType}
                    placeholder="All Types"
                    options={providerTypes.map(t => ({ value: t, label: t }))}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Source</label>
                  <FilterDropdown
                    value={filterSource}
                    onChange={setFilterSource}
                    placeholder="All Sources"
                    options={sources.map(s => ({ value: s._id, label: s.name }))}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Country</label>
                  <FilterDropdown
                    value={filterCountry}
                    onChange={setFilterCountry}
                    placeholder="All Countries"
                    options={countries.map(c => ({ value: c._id, label: formatCountryOptionLabel(c) }))}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Currency</label>
                  <FilterDropdown
                    value={filterCurrency}
                    onChange={setFilterCurrency}
                    placeholder="All Currencies"
                    options={currencies.map(c => ({ value: c._id, label: formatCurrencyOptionLabel(c) }))}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Category</label>
                  <FilterDropdown
                    value={filterCategory}
                    onChange={setFilterCategory}
                    placeholder="All Categories"
                    options={categories.map(c => ({ value: c._id, label: c.name }))}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Sub Category</label>
                  <FilterDropdown
                    value={filterSubCategory}
                    onChange={setFilterSubCategory}
                    placeholder="All Sub Categories"
                    options={subCategories.map(sc => ({ value: sc._id, label: sc.name }))}
                  />
                </div>

                <button type="button" className="btn btn-secondary" style={{ height: '38px', padding: '0 1.25rem', fontSize: '0.8125rem', whiteSpace: 'nowrap' }} onClick={() => {
                  setProviderSearchTerm('');
                  setFilterType('');
                  setFilterCategory('');
                  setFilterSubCategory('');
                  setFilterSource('');
                  setFilterCountry('');
                  setFilterCurrency('');
                }}>Reset Filters</button>
              </div>
            </div>
          </div>

          <div className="merchant-editor-game-table">
            {providersLoading && allProviders.length > 0 && (
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(255,255,255,0.7)', display: 'flex',
                alignItems: 'center', justifyContent: 'center', zIndex: 2
              }}>
                Loading...
              </div>
            )}
            <table className="table-striped" style={{ width: '100%', minWidth: canViewProviderFinancial ? '980px' : '820px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-light)', position: 'sticky', top: 0, backgroundColor: 'var(--table-header-bg)', zIndex: 1 }}>
                  <th style={{ padding: '0.625rem', textAlign: 'center', width: '50px' }}></th>
                  <SortableHeader
                    label="Provider"
                    sortKey="provider"
                    sort={providerSort}
                    onSortChange={handleProviderSortChange}
                    style={{ ...providerTableHeaderStyle, width: '280px' }}
                  />
                  <th style={{ padding: '0.625rem', textAlign: 'left', fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-secondary)' }}>Country</th>
                  <th style={{ padding: '0.625rem', textAlign: 'left', fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-secondary)' }}>Category</th>
                  {canViewProviderFinancial && (
                  <th style={{ padding: '0.625rem', textAlign: 'center', fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-secondary)' }}>Rate</th>
                  )}
                  <th style={{ padding: '0.625rem', textAlign: 'center', fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-secondary)' }}>Credentials</th>
                  {canViewProviderFinancial && (
                  <th style={{ padding: '0.625rem', textAlign: 'center', fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-secondary)' }}>Client Line</th>
                  )}
                  <th style={{ padding: '0.625rem', textAlign: 'center', fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-secondary)' }}>Status</th>
                  <th style={{ padding: '0.625rem', textAlign: 'right', fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-secondary)' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {allProviders.length === 0 ? (
                  <tr>
                    <td colSpan={canViewProviderFinancial ? 9 : 7} style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                      {providersLoading ? 'Loading providers...' : 'No providers found matching your filters.'}
                    </td>
                  </tr>
                ) : (
                  allProviders.map((p, index) => {
                    const assignedProvider = formData.providers.find(fp => fp.provider_id === p._id);
                    const isAssigned = !!assignedProvider;

                    return (
                      <tr key={p._id} style={{ borderBottom: index === allProviders.length - 1 ? 'none' : '1px solid var(--border-light)' }}>
                        <td style={{ padding: '0.625rem', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={isAssigned}
                            onChange={() => toggleProviderAssignment(p)}
                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                            disabled={!canManageProviders}
                          />
                        </td>
                        <td style={{ padding: '0.625rem', minWidth: '260px' }}>
                          <span style={{ display: 'block', fontSize: '0.875rem', fontWeight: 700 }}>{p.provider_id?.name || 'Unknown'}</span>
                          <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.2rem' }}>
                            {p.type || 'Any'} - {p.source?.name || 'Any'}
                          </span>
                        </td>
                        <td style={{ padding: '0.625rem' }}>
                          <span style={{ display: 'block', fontSize: '0.875rem', fontWeight: 700 }}>{getCountryCode(p.country)}</span>
                          <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.2rem' }}>
                            {getCurrencyCode(p.currency)}
                          </span>
                        </td>
                        <td style={{ padding: '0.625rem' }}>
                          <span style={{ display: 'block', fontSize: '0.875rem', fontWeight: 700 }}>{p.provider_id?.category?.name || '-'}</span>
                          <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.2rem' }}>
                            {p.provider_id?.sub_category?.name || '-'}
                          </span>
                        </td>
                        {canViewProviderFinancial && (
                          <td style={{ padding: '0.625rem', textAlign: 'center', fontSize: '0.875rem' }}>
                            {isAssigned ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                                {assignedProvider.selling_rate}%
                              </span>
                            ) : '-'}
                          </td>
                        )}
                        <td style={{ padding: '0.625rem', textAlign: 'center' }}>
                          {isAssigned ? (() => {
                            const credentialKeys = normalizeCredentialFields(p.api_credential_keys);
                            const boCredentialKeys = normalizeCredentialFields(p.bo_credential_keys);
                            const callbackDefs = Array.isArray(p.callback_urls) ? p.callback_urls : [];
                            const prodConfigured = isEnvComplete(assignedProvider.credentials?.production, [...credentialKeys, ...boCredentialKeys], callbackDefs);
                            const stgConfigured = isEnvComplete(assignedProvider.credentials?.staging, [...credentialKeys, ...boCredentialKeys], callbackDefs);

                            if (!prodConfigured && !stgConfigured) {
                              return <span style={{ color: 'var(--warning)' }}><AlertIcon size={16} /></span>;
                            }

                            return (
                              <span style={{ color: 'var(--success)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
                                <CheckCircleIcon size={16} />
                                {prodConfigured && stgConfigured ? (
                                  <span style={{ fontSize: '0.65rem', fontWeight: 600 }}>STG+PRD</span>
                                ) : prodConfigured ? (
                                  <span style={{ fontSize: '0.65rem', fontWeight: 600 }}>PRD</span>
                                ) : (
                                  <span style={{ fontSize: '0.65rem', fontWeight: 600 }}>STG</span>
                                )}
                              </span>
                            );
                          })() : <span style={{ color: 'var(--text-muted)' }}>-</span>}
                        </td>
                        {canViewProviderFinancial && (
                        <td style={{ padding: '0.625rem', textAlign: 'center' }}>
                          {isAssigned ? (
                            <span style={{
                              padding: '0.2rem 0.5rem', borderRadius: '9999px', fontSize: '0.7rem', fontWeight: 700,
                              backgroundColor: assignedProvider.is_client_line ? 'rgba(var(--primary-rgb), 0.12)' : 'rgba(var(--primary-rgb), 0.08)',
                              color: assignedProvider.is_client_line ? 'var(--accent)' : 'var(--text-muted)'
                            }}>{assignedProvider.is_client_line ? 'YES' : 'NO'}</span>
                          ) : '-'}
                        </td>
                        )}
                        <td style={{ padding: '0.625rem', textAlign: 'center' }}>
                          {isAssigned ? (() => {
                            const status = resolveProviderStatus(assignedProvider);
                            const isActive = status === 'active';
                            const isDraft = status === 'draft';
                            return (
                              <span style={{
                                padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 700,
                                backgroundColor: isActive ? 'rgba(16, 185, 129, 0.1)' : (isDraft ? 'rgba(251, 191, 36, 0.18)' : 'rgba(239, 68, 68, 0.1)'),
                                color: isActive ? 'var(--success)' : (isDraft ? 'var(--warning)' : 'var(--error)')
                              }}>{status.toUpperCase()}</span>
                            );
                          })() : '-'}
                        </td>
                        <td style={{ padding: '0.625rem', textAlign: 'right' }}>
                          <button
                            type="button"
                            className="btn"
                            style={{ padding: '0.3rem 0.6rem', fontSize: '0.75rem' }}
                            onClick={() => openProviderModal(assignedProvider)}
                            disabled={!isAssigned || !canManageProviders}
                          >Edit</button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
            <Pagination
              pagination={providersPagination}
              onPageChange={(pg) => fetchProviders(pg, providersPagination.limit, providerSort)}
              onLimitChange={(l) => {
                setProvidersPagination(prev => ({ ...prev, limit: l }));
                fetchProviders(1, l, providerSort);
              }}
              loading={providersLoading}
              itemName="providers"
            />
          </div>
        </section>

        {canEdit && (
          <footer className="merchant-editor-actions">
            <button type="button" className="btn btn-secondary" onClick={() => closeCurrentTabAndNavigate('/merchants')} disabled={submitting}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Saving...' : (editMode ? 'Save changes' : 'Create merchant')}
            </button>
          </footer>
        )}
      </form>

      <MerchantProviderConfigurationModal isOpen={isProviderModalOpen} onClose={closeProviderModal}
        editingProvider={editingProvider} setEditingProvider={setEditingProvider} modalMasterProvider={modalMasterProvider}
        canEdit={canManageProviders} canViewProviderFinancial={canViewProviderFinancial}
        canEditProviderFinancial={canEditProviderFinancial} onApply={saveProviderSettings} />
    </div>
  );
};

export default MerchantDetail;
